import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { Offer } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { ReasonField, TextField, reasonOk, splitList } from '@/components/ops/Forms';
import { LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits, parseDisplayedCredits } from '@/ops/credits';
import { formatBps, formatMinor, formatTimestamp, localInputToIso, parseMajorToMinor, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { TenantMultiPicker, TenantName } from '@/components/ops/TenantPicker';

type OfferKind = 'percent' | 'fixed' | 'bonus_credits' | 'free_months';

const offersKey = (env: string) => ['ops', 'billing', env, 'offers'] as const;

/** An offer's value in words — its unit depends on its kind (percent in bps, fixed in minor units…). */
export function offerValueText(offer: Pick<Offer, 'kind' | 'value' | 'currency'>, scale: number | null): string {
  switch (offer.kind) {
    case 'percent':
      return `${formatBps(offer.value)} off`;
    case 'fixed':
      return `${formatMinor(offer.value, offer.currency)} off`;
    case 'bonus_credits':
      return `+${formatCredits(offer.value, scale)}`;
    case 'free_months':
      return `${offer.value} free month(s)`;
    default:
      return String(offer.value);
  }
}

/**
 * Offers (plan §6.6): percent / fixed / bonus credits / free months, with eligibility, validity and
 * redemption limits, optionally behind a coupon code. Offers apply to MONEY SKUs only — never to a
 * credit-consumption rate. Expired or exhausted offers stay on the trail.
 */
export function OffersPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const offers = useQuery({ queryKey: offersKey(env ?? ''), queryFn: () => billingApi.offers(env!), enabled: !!env, ...LIVE });
  const { scale } = useCreditScale(env);
  const [creating, setCreating] = useState(false);
  const [expiring, setExpiring] = useState<Offer | null>(null);
  const [reason, setReason] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const expire = useMutation({
    mutationFn: () => billingApi.expireOffer(env!, expiring!.id, reason.trim()),
    onSuccess: () => { setExpiring(null); setReason(''); queryClient.invalidateQueries({ queryKey: offersKey(env!) }); },
  });

  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="Offers">
      <PageHeader title="Offers" subtitle="Promotions and coupons on plans and credit packs — one non-stackable offer per line."
        actions={
          <>
            <EnvironmentPicker env={env} environments={environments} onChange={setEnv} />
            {canManage && env && <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>New offer</button>}
          </>
        } />
      {env && (
        <QueryState query={offers} service="billing">
          {(data) => (
            <Section title="Offers" meta={`${data.offers.length} offer(s)`} flush>
              {data.offers.length === 0 ? (
                <div className="empty-state t-body">No offers.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Offer</th><th>Value</th><th>Applies to · eligibility</th><th>Validity</th><th className="num">Redemptions</th><th>State</th><th aria-label="Actions" /></tr></thead>
                    <tbody>
                      {data.offers.map((o) => (
                        <OfferRow key={o.id} env={env} offer={o} scale={scale} open={openId === o.id}
                          onToggle={() => setOpenId(openId === o.id ? null : o.id)}
                          onExpire={canManage && !o.expired_at ? () => setExpiring(o) : undefined} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}
        </QueryState>
      )}
      {creating && env && <OfferForm env={env} onClose={() => setCreating(false)} />}
      {expiring && (
        <ConfirmDialog title={`Expire “${expiring.name}”?`} confirmLabel="Expire offer" destructive pending={expire.isPending}
          confirmDisabled={!reasonOk(reason)} onConfirm={() => expire.mutate()} onCancel={() => { setExpiring(null); setReason(''); expire.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>No new redemptions from now on. Existing redemptions and the offer itself stay on the record.</p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={expire.error} service="billing" />
        </ConfirmDialog>
      )}
    </RequirePermission>
  );
}

function OfferRow({
  env, offer: o, scale, open, onToggle, onExpire,
}: {
  env: string;
  offer: Offer;
  scale: number | null;
  open: boolean;
  onToggle: () => void;
  onExpire?: () => void;
}) {
  return (
    <>
      <tr className={o.expired_at ? 'row-muted' : undefined}>
        <td>
          <div style={{ fontWeight: 600 }}>{o.name}</div>
          <div className="t-caption muted">{o.code ? <span className="mono">{o.code}</span> : 'automatic (no code)'} · {o.kind}</div>
        </td>
        <td>{offerValueText(o, scale)}</td>
        <td className="t-caption">
          {o.applies_to_kind === 'all' ? 'every money SKU' : `${o.applies_to_kind}: ${o.applies_to.join(', ')}`}
          <div className="muted">
            {o.eligibility === 'tenant_ids' ? <>tenants: {o.eligible_tenant_ids.map((id, i) => <span key={id}>{i > 0 && ', '}<TenantName env={env} id={id} link={false} /></span>)}</>
              : o.eligibility === 'plan_codes' ? `plans: ${o.eligible_plan_codes.join(', ')}`
                : o.eligibility.replace('_', ' ')}
          </div>
        </td>
        <td className="t-caption">{formatTimestamp(o.starts_at)} → {o.ends_at ? formatTimestamp(o.ends_at) : 'open'}</td>
        <td className="num">
          {o.redemptions_count}{o.max_redemptions !== null ? ` / ${o.max_redemptions}` : ''}
          {o.per_tenant_limit !== null && <div className="t-caption muted">≤ {o.per_tenant_limit} per tenant</div>}
        </td>
        <td><StatusChip status={o.state} /></td>
        <td>
          <div className="row">
            <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open} onClick={onToggle}>{open ? 'Hide' : 'Redemptions'}</button>
            {onExpire && <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={onExpire}>Expire</button>}
          </div>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={7}><Redemptions env={env} offerId={o.id} scale={scale} /></td></tr>
      )}
    </>
  );
}

function Redemptions({ env, offerId, scale }: { env: string; offerId: string; scale: number | null }) {
  const list = useInfiniteQuery({
    queryKey: ['ops', 'billing', env, 'redemptions', offerId],
    queryFn: ({ pageParam }) => billingApi.redemptions(env, offerId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });
  const rows = list.data?.pages.flatMap((p) => p.redemptions) ?? [];
  const first = list.data?.pages[0];

  return (
    <QueryState query={{ ...list, data: list.data ? rows : undefined }} service="billing">
      {(items) => (
        <>
          <p className="t-caption muted" style={{ margin: '0 0 8px' }}>
            {first?.redemptions_count ?? 0} redemption(s){first?.max_redemptions != null ? ` of ${first.max_redemptions} allowed` : ''}.
          </p>
          {items.length === 0 ? <div className="t-caption subtle">None yet.</div> : (
            <table className="table">
              <thead><tr><th>When</th><th>Tenant</th><th>SKU</th><th className="num">Discount</th><th className="num">Bonus</th><th>On</th></tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <td className="t-caption">{formatTimestamp(r.redeemed_at)}</td>
                    <td className="t-caption"><TenantName env={env} id={r.tenant_id} tab="billing" /></td>
                    <td className="mono t-caption">{r.sku}</td>
                    <td className="num">{r.discount_minor ? formatMinor(r.discount_minor, r.currency) : '—'}</td>
                    <td className="num">{r.bonus_credits ? formatCredits(r.bonus_credits, scale) : '—'}</td>
                    <td className="t-caption mono">{r.invoice_id ? `invoice ${shortId(r.invoice_id)}` : r.purchase_id ? `purchase ${shortId(r.purchase_id)}` : r.line_ref}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <LoadMore hasMore={!!list.hasNextPage} loading={list.isFetchingNextPage} onLoad={() => list.fetchNextPage()} />
        </>
      )}
    </QueryState>
  );
}

function OfferForm({ env, onClose }: { env: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { scale, status } = useCreditScale(env);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<OfferKind>('percent');
  const [value, setValue] = useState('');
  const [currency, setCurrency] = useState('EGP');
  const [appliesToKind, setAppliesToKind] = useState<'all' | 'skus' | 'plans'>('all');
  const [appliesTo, setAppliesTo] = useState('');
  const [eligibility, setEligibility] = useState<'all' | 'new_tenants' | 'tenant_ids' | 'plan_codes'>('all');
  const [eligibleList, setEligibleList] = useState('');
  const [eligibleTenants, setEligibleTenants] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [perTenant, setPerTenant] = useState('');
  const [reason, setReason] = useState('');

  // The value in the API's unit for the kind: bps, minor units, micro-credits, months.
  const parsedValue = ((): { ok: true; value: number } | { ok: false; error: string } => {
    if (kind === 'percent') {
      const bps = Math.round(Number(value) * 100);
      return Number.isFinite(bps) && bps >= 1 && bps <= 10000 ? { ok: true, value: bps } : { ok: false, error: '0.01–100 %.' };
    }
    if (kind === 'fixed') {
      const p = parseMajorToMinor(value, currency.toUpperCase());
      return p.ok ? (p.minor > 0 ? { ok: true, value: p.minor } : { ok: false, error: 'Above zero.' }) : p;
    }
    if (kind === 'bonus_credits') {
      const p = parseDisplayedCredits(value, scale);
      return p.ok ? (p.micro > 0 ? { ok: true, value: p.micro } : { ok: false, error: 'Above zero.' }) : p;
    }
    const months = Number(value);
    return Number.isInteger(months) && months >= 1 && months <= 120 ? { ok: true, value: months } : { ok: false, error: '1–120 months.' };
  })();

  const targets = splitList(appliesTo);
  // Tenants are chosen by name; plan codes are typed.
  const eligible = eligibility === 'tenant_ids' ? eligibleTenants : splitList(eligibleList);
  const intOrNull = (text: string) => (text.trim() === '' ? null : Number(text));
  const max = intOrNull(maxRedemptions);
  const per = intOrNull(perTenant);
  const problems = [
    !name.trim() && 'A name.',
    code && !/^[A-Za-z0-9_-]{3,64}$/.test(code) && 'Code: 3–64 letters, digits, - and _.',
    !parsedValue.ok && `Value: ${parsedValue.error}`,
    kind === 'fixed' && !/^[A-Za-z]{3}$/.test(currency) && 'A fixed offer needs an ISO currency.',
    appliesToKind !== 'all' && targets.length === 0 && `List the ${appliesToKind === 'skus' ? 'SKUs' : 'plan codes'} it applies to.`,
    (kind === 'bonus_credits' || kind === 'free_months') && appliesToKind === 'all' &&
      `A ${kind} offer must target ${kind === 'bonus_credits' ? 'credit-pack SKUs' : 'plans'} explicitly.`,
    (eligibility === 'tenant_ids' || eligibility === 'plan_codes') && eligible.length === 0 && 'List who is eligible.',
    max !== null && (!Number.isInteger(max) || max <= 0) && 'Max redemptions: a positive whole number.',
    per !== null && (!Number.isInteger(per) || per <= 0) && 'Per-tenant limit: a positive whole number.',
    !reasonOk(reason) && 'A reason.',
  ].filter(Boolean) as string[];

  const create = useMutation({
    mutationFn: () => billingApi.createOffer(env, {
      name: name.trim(),
      code: code.trim() || undefined,
      kind,
      value: parsedValue.ok ? parsedValue.value : 0,
      currency: kind === 'fixed' ? currency.toUpperCase() : undefined,
      applies_to_kind: appliesToKind,
      applies_to: appliesToKind === 'all' ? [] : targets,
      eligibility,
      eligible_tenant_ids: eligibility === 'tenant_ids' ? eligible : [],
      eligible_plan_codes: eligibility === 'plan_codes' ? eligible : [],
      starts_at: localInputToIso(startsAt) ?? undefined,
      ends_at: localInputToIso(endsAt) ?? undefined,
      max_redemptions: max,
      per_tenant_limit: per,
      reason: reason.trim(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: offersKey(env) }); onClose(); },
  });

  return (
    <Modal title="New offer" size="lg" onClose={create.isPending ? undefined : onClose} dismissible={!create.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={problems.length > 0 || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create offer'}
          </button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Name" value={name} onChange={setName} required />
        <TextField label="Coupon code (optional)" value={code} onChange={setCode} mono hint="Case-insensitive, unique. Empty = applied automatically when eligible." />
      </div>
      <Segmented label="Kind" value={kind} onChange={(v) => { setKind(v); setValue(''); }}
        options={[{ value: 'percent', label: 'Percent' }, { value: 'fixed', label: 'Fixed amount' }, { value: 'bonus_credits', label: 'Bonus credits' }, { value: 'free_months', label: 'Free months' }]} />
      <div className="form-grid">
        <TextField
          label={kind === 'percent' ? 'Discount (%)' : kind === 'fixed' ? `Discount (${currency.toUpperCase()})` : kind === 'bonus_credits' ? 'Bonus (displayed credits)' : 'Months'}
          value={value}
          onChange={setValue}
          disabled={kind === 'bonus_credits' && status !== 'ok'}
          error={value && !parsedValue.ok ? parsedValue.error : null}
          hint={kind === 'bonus_credits' ? scaleUnavailableReason(status) ?? 'Sent as micro-credits.' : kind === 'percent' ? 'Sent in basis points.' : undefined}
        />
        {kind === 'fixed' && <TextField label="Currency" value={currency} onChange={setCurrency} mono />}
      </div>
      <div className="form-grid">
        <Select label="Applies to" value={appliesToKind} onChange={(v) => setAppliesToKind(v as typeof appliesToKind)} searchable={false}
          options={[{ value: 'all', label: 'Every money SKU' }, { value: 'skus', label: 'Listed SKUs' }, { value: 'plans', label: 'Listed plans' }]} />
        {appliesToKind !== 'all' && (
          <TextField label={appliesToKind === 'skus' ? 'SKUs (or prefix.*)' : 'Plan codes'} value={appliesTo} onChange={setAppliesTo} mono
            placeholder={appliesToKind === 'skus' ? 'credits.pack.communications.*' : 'pro, team'} hint="Comma separated. Never a CREDITS consumption SKU." />
        )}
        <Select label="Eligibility" value={eligibility} onChange={(v) => setEligibility(v as typeof eligibility)} searchable={false}
          options={[{ value: 'all', label: 'Everyone' }, { value: 'new_tenants', label: 'New tenants' }, { value: 'tenant_ids', label: 'Listed tenants' }, { value: 'plan_codes', label: 'Tenants on listed plans' }]} />
        {eligibility === 'tenant_ids' && (
          <TenantMultiPicker env={env} value={eligibleTenants} onChange={setEligibleTenants} label="Eligible tenants" />
        )}
        {eligibility === 'plan_codes' && (
          <TextField label="Plan codes" value={eligibleList} onChange={setEligibleList} mono hint="Comma separated." />
        )}
        <TextField label="Starts (your local time)" value={startsAt} onChange={setStartsAt} type="datetime-local" hint="Empty = now." />
        <TextField label="Ends (your local time)" value={endsAt} onChange={setEndsAt} type="datetime-local" hint="Empty = open-ended." />
        <TextField label="Max redemptions" value={maxRedemptions} onChange={setMaxRedemptions} type="number" min={1} hint="Empty = unlimited." />
        <TextField label="Per-tenant limit" value={perTenant} onChange={setPerTenant} type="number" min={1} hint="Empty = unlimited." />
      </div>
      <ReasonField value={reason} onChange={setReason} />
      {problems.length > 0 && name && <span className="t-caption muted">{problems[0]}</span>}
      <MutationError error={create.error} service="billing" />
    </Modal>
  );
}
